import { ReactNode, useMemo, useState } from 'react';

import { useClientTeamMemberFilter } from '../hooks/useClientTeamMemberFilter';
import { AutoCompleteOption } from '../types/AutoCompleteOption';
import { ClientTeamMemberBase } from '../types/ClientTeamMember';
import { buildClientTeamMemberDisplayName } from '../utils/buildClientTeamMemberDisplayName';

import { AutoComplete } from './AutoComplete';

/**
 * Props for ClientTeamMemberAutoComplete component
 *
 * This component provides a specialized autocomplete for selecting client team members/guests
 * with built-in search, filtering, and optional footer content for actions like creating new guests.
 */
export interface ClientTeamMemberAutoCompleteProps<
  T extends ClientTeamMemberBase,
> {
  /**
   * Array of guest options wrapped with metadata
   *
   * @example
   * ```typescript
   * const guests = guestData.map(guest => ({
   *   value: guest,
   *   metadata: {
   *     disabled: alreadySelectedIds.has(guest.id),
   *     label: getLocationLabel(guest.id)
   *   }
   * }));
   * ```
   */
  guests: Array<AutoCompleteOption<T>>;

  /** Currently selected guest (unwrapped value) */
  value: T | null;

  /** Callback when selection changes (receives unwrapped value) */
  onChange: (guest: T | null) => void;

  /** Label text for the autocomplete field */
  label: string;

  /** Placeholder text shown in the input */
  placeholder?: string;

  /** Disable the entire component */
  disabled?: boolean;

  /** Show loading state in dropdown */
  loading?: boolean;

  /**
   * Optional footer content rendered at the bottom of the dropdown
   * Use this for actions like "Create New Guest" or "Unknown Guest" buttons
   *
   * @example
   * ```tsx
   * footer={
   *   <div className="space-y-2 p-3">
   *     <Button onClick={() => selectUnknownGuest()}>Unknown Guest</Button>
   *     <Button onClick={() => openCreateModal()}>Create New Guest</Button>
   *   </div>
   * }
   * ```
   */
  footer?: ReactNode;

  /**
   * Optional callback when search query changes
   * Useful for external state synchronization or analytics
   */
  onQueryChange?: (query: string) => void;
}

/**
 * ClientTeamMemberAutoComplete
 *
 * A specialized autocomplete component for selecting client team members/guests.
 * This is a controlled component that handles search/filtering internally while
 * delegating selection state to the parent.
 *
 * Features:
 * - Automatic filtering by name or semi-unique identifier (DoD ID)
 * - Metadata support via wrapper pattern (disabled state, labels)
 * - Composable footer for custom actions
 * - Loading state support
 *
 * @example
 * Basic usage:
 * ```tsx
 * const guestOptions = guests.map(g => ({ value: g }));
 *
 * <ClientTeamMemberAutoComplete
 *   guests={guestOptions}
 *   value={selectedGuest}
 *   onChange={setSelectedGuest}
 *   label="Select Guest"
 *   placeholder="Search by name or ID"
 * />
 * ```
 *
 * @example
 * With disabled guests and metadata:
 * ```tsx
 * const guestOptions = guests.map(guest => ({
 *   value: guest,
 *   metadata: {
 *     disabled: alreadySelectedIds.has(guest.id),
 *     label: guestLocationMap.get(guest.id)
 *       ? `(Already in Room ${guestLocationMap.get(guest.id).room})`
 *       : undefined
 *   }
 * }));
 *
 * <ClientTeamMemberAutoComplete
 *   guests={guestOptions}
 *   value={selectedGuest}
 *   onChange={setSelectedGuest}
 *   label="Select Guest"
 *   footer={
 *     <Button onClick={openCreateGuestModal}>Create New Guest</Button>
 *   }
 * />
 * ```
 */
export const ClientTeamMemberAutoComplete = <T extends ClientTeamMemberBase>({
  guests,
  value,
  onChange,
  label,
  placeholder = 'Search by name or ID...',
  disabled = false,
  loading = false,
  footer,
  onQueryChange,
}: ClientTeamMemberAutoCompleteProps<T>) => {
  // Internal search query state
  const [query, setQuery] = useState('');

  // Filter guests based on search query using our custom hook
  const filteredGuests = useClientTeamMemberFilter(guests, query);

  // Ensure selected value is always in the results (even if filtered out)
  // This prevents blank rows when the selected guest doesn't match the current filter
  const dataWithSelectedValue = useMemo(() => {
    // If no value selected, just return filtered results
    if (!value) return filteredGuests;

    // Check if selected value is already in filtered results
    const isSelectedInFiltered = filteredGuests.some(
      (opt) => opt.value.id === value.id,
    );

    // If already there, return as-is
    if (isSelectedInFiltered) return filteredGuests;

    // Otherwise, find the selected option in the original guests array
    const selectedOption = guests.find((opt) => opt.value.id === value.id);

    // Add it to the beginning of filtered results
    return selectedOption
      ? [selectedOption, ...filteredGuests]
      : filteredGuests;
  }, [filteredGuests, value, guests]);

  // Handle query changes
  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    onQueryChange?.(newQuery);
  };

  return (
    <AutoComplete
      data={dataWithSelectedValue}
      value={value}
      onChange={onChange}
      displayValue={(guest) =>
        guest ? buildClientTeamMemberDisplayName(guest) : ''
      }
      renderOption={(guest) => (
        <span>{buildClientTeamMemberDisplayName(guest)}</span>
      )}
      labelText={label}
      placeholder={placeholder}
      disabled={disabled}
      loading={loading}
      onQueryChange={handleQueryChange}
      renderFooter={footer ? () => footer : undefined}
    />
  );
};
